// Re-sincronização da push subscription com o backend.
//
// Por que existe: o navegador rotaciona/expira a push subscription de tempos em
// tempos. O Service Worker re-inscreve (ver sw.js → pushsubscriptionchange), mas
// NÃO pode registrar o endpoint novo no backend porque não tem o Bearer token
// (fica no localStorage da página). Estas funções fecham esse gap a partir da
// página (autenticada):
//   - initPushResync(): ouve a mensagem do SW (rotação com aba aberta) e faz um
//     re-sync no boot (rede de segurança quando não havia aba aberta na rotação).
//   - resyncPushSubscription(): re-registra a subscription ATUAL (idempotente).
//     NÃO pede permissão nem cria subscription nova — só sincroniza a existente.
import { pushSubscriptionsApi, type PushSubscriptionPayload } from './pushSubscriptions'

function toPayload(json: PushSubscriptionJSON | null | undefined): PushSubscriptionPayload | null {
  if (!json || !json.endpoint || !json.keys?.p256dh || !json.keys?.auth) return null
  return {
    endpoint: json.endpoint,
    p256dh: json.keys.p256dh,
    auth: json.keys.auth,
    user_agent: navigator.userAgent,
  }
}

/** Re-registra a subscription atual no backend (upsert idempotente). Best-effort:
 *  só age se o usuário já concedeu permissão, está logado e há subscription. */
export async function resyncPushSubscription(): Promise<void> {
  try {
    if (typeof Notification === 'undefined' || Notification.permission !== 'granted') return
    if (!localStorage.getItem('session_token')) return
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) return
    const reg = await navigator.serviceWorker.ready
    const sub = await reg.pushManager.getSubscription()
    const payload = toPayload(sub?.toJSON())
    if (payload) await pushSubscriptionsApi.register(payload)
  } catch {
    /* best-effort — não quebra o boot do app */
  }
}

/** Liga o re-sync: ouvinte da mensagem do SW (rotação) + re-sync no boot. */
export function initPushResync(): void {
  if (!('serviceWorker' in navigator)) return
  navigator.serviceWorker.addEventListener('message', (event: MessageEvent) => {
    const data = event.data as { type?: string; subscription?: PushSubscriptionJSON } | null
    if (!data || data.type !== 'consoleai-push-resubscribed') return
    const payload = toPayload(data.subscription)
    if (payload) pushSubscriptionsApi.register(payload).catch(() => { /* best-effort */ })
  })
  void resyncPushSubscription()
}

// Exportado para teste unitário do mapeamento puro.
export const __test = { toPayload }
